import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';

/**
 * @typedef {Object} Reseller
 * @property {string|number} id - The reseller ID.
 * @property {string} [name] - The reseller name.
 * @property {string} [address] - The street address.
 * @property {string} [postal_code] - The postal/zip code.
 * @property {string} [city] - The city name.
 * @property {string} [country] - The country name.
 * @property {string|number} [latitude] - The latitude coordinate.
 * @property {string|number} [longitude] - The longitude coordinate.
 * @property {string} [phone] - The phone number.
 * @property {string} [email] - The email address.
 * @property {string} [opening_hours] - The opening hours.
 * @property {string} [store_type] - The store type key (own, retail, btob, salon).
 */

/**
 * @typedef {Object} MarkerIcon
 * @property {string} url - The icon image URL.
 * @property {number} width - The icon width in pixels.
 * @property {number} height - The icon height in pixels.
 */

/**
 * @typedef {Object} MapConfig
 * @property {string} provider - The map provider: 'leaflet', 'google' or 'mapbox'.
 * @property {Object} config - The provider specific configuration.
 */

/**
 * Reseller map custom element.
 *
 * Loads reseller locations from an external API and renders them as a list and
 * as markers on a map. The map provider (Leaflet, Google Maps or Mapbox) is
 * resolved at runtime from the merchant's map-config endpoint, falling back to
 * OpenStreetMap/Leaflet when the endpoint is unavailable.
 *
 * @typedef {Object} ResellerMapRefs
 * @property {HTMLElement} listContainer - The list container element.
 * @property {HTMLElement} mapContainer - The map container element.
 * @property {HTMLInputElement} [searchInput] - The search input element.
 * @property {HTMLButtonElement} [searchButton] - The search submit button.
 * @property {HTMLElement} [loadingState] - The loading state element.
 * @property {HTMLElement} [emptyState] - The empty state element.
 * @property {HTMLElement} [liveRegion] - ARIA live region for announcements.
 *
 * @extends {Component<ResellerMapRefs>}
 */
class ResellerMap extends Component {
  requiredRefs = ['listContainer', 'mapContainer'];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {*} */
  #map = null;

  /** @type {*[]} */
  #markers = [];

  /** @type {*} */
  #searchMarker = null;

  /** @type {Reseller[]} */
  #resellers = [];

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {MapConfig | null} */
  #mapConfig = null;

  /** @type {Record<string, MarkerIcon>} */
  #markerIcons = {};

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#initSearch();
    this.#observeVisibility();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#visibilityObserver?.disconnect();
    this.#map?.remove?.();

    super.disconnectedCallback();
  }

  /**
   * The configured resellers API endpoint.
   * @returns {string}
   */
  get #apiUrl() {
    return this.dataset.apiUrl || '';
  }

  /**
   * Derives a sibling API endpoint from the resellers endpoint.
   * @param {string} endpoint - The endpoint filename, e.g. 'map-config.php'.
   * @returns {string}
   */
  #siblingEndpoint(endpoint) {
    return this.#apiUrl.replace(/resellers\.php/, endpoint);
  }

  /**
   * Lazy-loads the map and reseller data once the element scrolls into view.
   */
  #observeVisibility() {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          this.#visibilityObserver?.disconnect();
          this.#init();
        }
      },
      { threshold: 0.1 },
    );

    this.#visibilityObserver.observe(this);
  }

  /**
   * Loads configuration and data, then initialises the map and list.
   */
  async #init() {
    const [config] = await Promise.all([this.#loadMapConfig(), this.#loadMarkerIcons()]);
    this.#mapConfig = config;

    try {
      await this.#initMap(config);
    } catch (error) {
      console.warn('Reseller map failed to initialise:', error);
    }

    await this.#loadResellers();
  }

  /**
   * Loads the map configuration, falling back to OpenStreetMap/Leaflet.
   * @returns {Promise<MapConfig>}
   */
  async #loadMapConfig() {
    const fallback = {
      provider: 'leaflet',
      config: {
        tile_url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '&copy; OpenStreetMap contributors',
        center: [52.1326, 5.2913],
        zoom: 7,
        max_zoom: 19,
      },
    };

    try {
      const data = await this.#fetchJson(this.#withParams(this.#siblingEndpoint('map-config.php')));
      if (!data?.success) return fallback;
      return data;
    } catch (error) {
      console.warn('Reseller map config unavailable, using OpenStreetMap:', error);
      return fallback;
    }
  }

  /**
   * Loads the custom marker icons keyed by store type.
   */
  async #loadMarkerIcons() {
    try {
      const data = await this.#fetchJson(this.#withParams(this.#siblingEndpoint('marker-icons.php')));
      if (data?.success && data.icons) {
        this.#markerIcons = data.icons;
      }
    } catch (error) {
      console.warn('Reseller map marker icons unavailable:', error);
    }
  }

  /**
   * Loads the resellers for the configured store types.
   */
  async #loadResellers() {
    try {
      const data = await this.#fetchJson(this.#withParams(this.#apiUrl, { types: this.dataset.storeTypes }));

      if (data?.success && Array.isArray(data.resellers) && data.resellers.length > 0) {
        this.#resellers = data.resellers;
        this.#renderList(this.#resellers);
        this.#renderMarkers(this.#resellers);
        this.#fitToResellers(this.#resellers);
      } else {
        this.#showEmpty();
      }
    } catch (error) {
      console.warn('Reseller map failed to load resellers:', error);
      this.#showEmpty();
    }
  }

  /**
   * Builds a URL with the API key and optional extra params appended.
   * @param {string} url - The base URL.
   * @param {Record<string, string|undefined>} [extra] - Extra query params.
   * @returns {string}
   */
  #withParams(url, extra = {}) {
    if (!url) return url;
    const params = new URLSearchParams();
    if (this.dataset.apiKey) params.append('api_key', this.dataset.apiKey);
    for (const [key, value] of Object.entries(extra)) {
      if (value) params.append(key, value);
    }
    const query = params.toString();
    return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
  }

  /**
   * Fetches and parses JSON for the given URL using the shared abort signal.
   * @param {string} url - The URL to fetch.
   * @returns {Promise<*>}
   */
  async #fetchJson(url) {
    const response = await fetch(url, { signal: this.#controller.signal });
    return response.json();
  }

  /**
   * Initialises the map for the resolved provider.
   * @param {MapConfig} config - The resolved map configuration.
   */
  async #initMap(config) {
    if (config.provider === 'google') {
      await this.#initGoogleMap(config.config);
    } else if (config.provider === 'mapbox') {
      await this.#initMapboxMap(config.config);
    } else {
      await this.#initLeafletMap(config.config);
    }
  }

  /**
   * Initialises a Leaflet (OpenStreetMap) map.
   * @param {*} config - The Leaflet configuration.
   */
  async #initLeafletMap(config) {
    if (typeof window.L === 'undefined') {
      await this.#loadStylesheet('https://unpkg.com/leaflet@1.9.4/dist/leaflet.css');
      await this.#loadScript('https://unpkg.com/leaflet@1.9.4/dist/leaflet.js');
    }

    const { L } = window;
    this.#map = L.map(this.refs.mapContainer, { scrollWheelZoom: true }).setView(
      config.center || [52.1326, 5.2913],
      config.zoom || 7,
    );

    L.tileLayer(config.tile_url, {
      attribution: config.attribution,
      maxZoom: config.max_zoom || 19,
    }).addTo(this.#map);

    // Leaflet needs a size recalculation once the container is laid out.
    requestAnimationFrame(() => this.#map?.invalidateSize());
  }

  /**
   * Initialises a Google Maps map.
   * @param {*} config - The Google Maps configuration.
   */
  async #initGoogleMap(config) {
    if (!config.api_key) throw new Error('Google Maps API key not configured');

    await this.#loadScript(`https://maps.googleapis.com/maps/api/js?key=${config.api_key}`);

    this.#map = new window.google.maps.Map(this.refs.mapContainer, {
      center: { lat: config.center?.lat ?? 52.1326, lng: config.center?.lng ?? 5.2913 },
      zoom: config.zoom || 7,
      mapTypeControl: false,
    });
  }

  /**
   * Initialises a Mapbox GL JS map.
   * @param {*} config - The Mapbox configuration.
   */
  async #initMapboxMap(config) {
    if (!config.access_token) throw new Error('Mapbox access token not configured');

    if (typeof window.mapboxgl === 'undefined') {
      await this.#loadStylesheet('https://api.mapbox.com/mapbox-gl-js/v3.9.3/mapbox-gl.css');
      await this.#loadScript('https://api.mapbox.com/mapbox-gl-js/v3.9.3/mapbox-gl.js');
    }

    window.mapboxgl.accessToken = config.access_token;
    this.#map = new window.mapboxgl.Map({
      container: this.refs.mapContainer,
      style: config.style || 'mapbox://styles/mapbox/light-v11',
      center: config.center || [5.2913, 52.1326],
      zoom: config.zoom || 6,
    });
  }

  /**
   * Renders markers for the given resellers.
   * @param {Reseller[]} resellers - The resellers to render.
   */
  #renderMarkers(resellers) {
    if (!this.#map) return;
    this.#clearMarkers();

    for (const reseller of resellers) {
      const lat = parseFloat(String(reseller.latitude));
      const lng = parseFloat(String(reseller.longitude));
      if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

      const marker = this.#createMarker(reseller, lat, lng);
      if (marker) this.#markers.push(marker);
    }
  }

  /**
   * Creates a single marker for the active provider.
   * @param {Reseller} reseller - The reseller.
   * @param {number} lat - Latitude.
   * @param {number} lng - Longitude.
   * @returns {*}
   */
  #createMarker(reseller, lat, lng) {
    const provider = this.#mapConfig?.provider;
    const icon = this.#markerIcons[reseller.store_type ?? ''];

    try {
      if (provider === 'google') {
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: this.#map,
          title: reseller.name,
          icon: icon
            ? {
                url: icon.url,
                scaledSize: new window.google.maps.Size(icon.width, icon.height),
                anchor: new window.google.maps.Point(icon.width / 2, icon.height),
              }
            : undefined,
        });
        const infoWindow = new window.google.maps.InfoWindow({ content: this.#popupHtml(reseller) });
        marker.addListener('click', () => {
          infoWindow.open(this.#map, marker);
          this.#selectReseller(String(reseller.id), false);
        });
        return marker;
      }

      if (provider === 'mapbox') {
        const element = document.createElement('div');
        element.style.cursor = 'pointer';
        element.style.backgroundSize = '100%';
        element.style.width = `${icon?.width ?? 25}px`;
        element.style.height = `${icon?.height ?? 41}px`;
        if (icon) element.style.backgroundImage = `url(${icon.url})`;

        const marker = new window.mapboxgl.Marker(element)
          .setLngLat([lng, lat])
          .setPopup(new window.mapboxgl.Popup({ offset: 25 }).setHTML(this.#popupHtml(reseller)))
          .addTo(this.#map);
        element.addEventListener('click', () => this.#selectReseller(String(reseller.id), false), {
          signal: this.#controller.signal,
        });
        return marker;
      }

      // Leaflet (default)
      const { L } = window;
      const options = icon
        ? {
            icon: L.icon({
              iconUrl: icon.url,
              iconSize: [icon.width, icon.height],
              iconAnchor: [icon.width / 2, icon.height],
              popupAnchor: [0, -icon.height],
            }),
          }
        : {};
      const marker = L.marker([lat, lng], { title: reseller.name, ...options }).addTo(this.#map);
      marker.bindPopup(this.#popupHtml(reseller));
      marker.on('click', () => this.#selectReseller(String(reseller.id), false));
      marker._resellerId = String(reseller.id);
      return marker;
    } catch (error) {
      console.warn('Reseller map failed to create marker:', reseller.id, error);
      return null;
    }
  }

  /**
   * Removes all reseller markers from the map.
   */
  #clearMarkers() {
    const provider = this.#mapConfig?.provider;
    for (const marker of this.#markers) {
      if (provider === 'google') marker.setMap(null);
      else if (provider === 'mapbox') marker.remove();
      else this.#map?.removeLayer(marker);
    }
    this.#markers = [];
  }

  /**
   * Fits the map view to the given resellers.
   * @param {Reseller[]} resellers - The resellers to fit.
   */
  #fitToResellers(resellers) {
    if (!this.#map) return;
    const coords = resellers
      .map((reseller) => [parseFloat(String(reseller.latitude)), parseFloat(String(reseller.longitude))])
      .filter(([lat, lng]) => !Number.isNaN(lat) && !Number.isNaN(lng));
    if (coords.length === 0) return;

    const provider = this.#mapConfig?.provider;
    try {
      if (provider === 'google') {
        const bounds = new window.google.maps.LatLngBounds();
        coords.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
        this.#map.fitBounds(bounds);
      } else if (provider === 'mapbox') {
        const bounds = new window.mapboxgl.LngLatBounds();
        coords.forEach(([lat, lng]) => bounds.extend([lng, lat]));
        this.#map.fitBounds(bounds, { padding: 50, maxZoom: 12 });
      } else {
        this.#map.fitBounds(coords, { padding: [50, 50], maxZoom: 12 });
      }
    } catch (error) {
      console.warn('Reseller map failed to fit bounds:', error);
    }
  }

  /**
   * Renders the reseller list.
   * @param {Reseller[]} resellers - The resellers to render.
   */
  #renderList(resellers) {
    const { listContainer, loadingState, emptyState } = this.refs;
    loadingState?.remove();

    this.#items.forEach((item) => item.remove());
    this.#items = [];

    if (emptyState) emptyState.hidden = resellers.length > 0;
    if (resellers.length === 0) return;

    const fragment = document.createDocumentFragment();
    for (const reseller of resellers) {
      fragment.appendChild(this.#buildItem(reseller));
    }
    listContainer.insertBefore(fragment, emptyState ?? null);
    this.#items = /** @type {HTMLElement[]} */ ([...listContainer.querySelectorAll('[data-reseller-id]')]);
  }

  /**
   * Builds a single reseller list item.
   * @param {Reseller} reseller - The reseller.
   * @returns {HTMLLIElement}
   */
  #buildItem(reseller) {
    const item = document.createElement('li');
    item.className = 'apc-reseller-map__item';
    item.dataset.resellerId = String(reseller.id);
    item.setAttribute('role', 'listitem');
    item.setAttribute('tabindex', '0');
    item.setAttribute('aria-selected', 'false');

    const lines = [
      reseller.address,
      [reseller.postal_code, reseller.city].filter(Boolean).join(' '),
      reseller.country,
      reseller.phone,
      reseller.email,
    ]
      .filter(Boolean)
      .map((line) => `<span>${escapeHtml(String(line))}</span>`)
      .join('');

    item.innerHTML = `
      <span class="apc-reseller-map__item-name">${escapeHtml(reseller.name ?? '')}</span>
      <span class="apc-reseller-map__item-address">${lines}</span>
      <span class="apc-reseller-map__item-actions">
        <button type="button" class="apc-reseller-map__item-action" data-view-on-map>${escapeHtml(
          this.dataset.viewOnMapText ?? 'View on map',
        )}</button>
        <a class="apc-reseller-map__item-action" href="${this.#directionsUrl(reseller)}" target="_blank" rel="noopener">${escapeHtml(
          this.dataset.directionsText ?? 'Directions',
        )}</a>
      </span>
    `;

    item.addEventListener('click', () => this.#selectReseller(String(reseller.id)), {
      signal: this.#controller.signal,
    });
    item.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.#selectReseller(String(reseller.id));
        }
      },
      { signal: this.#controller.signal },
    );

    const viewOnMap = item.querySelector('[data-view-on-map]');
    viewOnMap?.addEventListener(
      'click',
      (event) => {
        event.stopPropagation();
        this.#selectReseller(String(reseller.id));
      },
      { signal: this.#controller.signal },
    );

    return item;
  }

  /**
   * Selects a reseller, highlighting its list item and centring the map.
   * @param {string} resellerId - The reseller ID.
   * @param {boolean} [flyTo=true] - Whether to recentre the map.
   */
  #selectReseller(resellerId, flyTo = true) {
    this.#items.forEach((item) => {
      const selected = item.dataset.resellerId === resellerId;
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) item.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
    });

    if (!flyTo || !this.#map) return;

    const reseller = this.#resellers.find((entry) => String(entry.id) === resellerId);
    if (!reseller) return;

    const lat = parseFloat(String(reseller.latitude));
    const lng = parseFloat(String(reseller.longitude));
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const provider = this.#mapConfig?.provider;
    if (provider === 'google') {
      this.#map.setCenter({ lat, lng });
      this.#map.setZoom(14);
    } else if (provider === 'mapbox') {
      this.#map.flyTo({ center: [lng, lat], zoom: 14, duration: prefersReducedMotion() ? 0 : 1200 });
    } else {
      this.#map.setView([lat, lng], 14);
    }
  }

  /**
   * Initialises the search input and submit button.
   */
  #initSearch() {
    const { searchInput, searchButton } = this.refs;
    if (!searchInput) return;

    const runSearch = () => {
      const query = searchInput.value.trim();
      if (query) this.#searchLocation(query);
    };

    searchInput.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          runSearch();
        }
      },
      { signal: this.#controller.signal },
    );
    searchButton?.addEventListener('click', runSearch, { signal: this.#controller.signal });
  }

  /**
   * Geocodes a query and shows the nearest resellers.
   * @param {string} query - The search query (postal code or place).
   */
  #searchLocation = debounce(async (query) => {
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query,
      )}&countrycodes=nl,be&limit=1`;
      const results = await this.#fetchJson(url);

      if (!Array.isArray(results) || results.length === 0) {
        this.#announce(this.dataset.notFoundText || 'Location not found');
        return;
      }

      const lat = parseFloat(results[0].lat);
      const lng = parseFloat(results[0].lon);
      const nearest = [...this.#resellers]
        .map((reseller) => ({
          reseller,
          distance: haversine(lat, lng, parseFloat(String(reseller.latitude)), parseFloat(String(reseller.longitude))),
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 10)
        .map((entry) => entry.reseller);

      this.#renderList(nearest);
      this.#renderMarkers(nearest);
      this.#centreOnSearch(lat, lng, results[0].display_name);
      this.#announceResults(nearest.length);
    } catch (error) {
      console.warn('Reseller map search failed:', error);
      this.#announce(this.dataset.errorText || 'Search failed');
    }
  }, 300);

  /**
   * Centres the map on a search location and drops a temporary marker.
   * @param {number} lat - Latitude.
   * @param {number} lng - Longitude.
   * @param {string} label - The location label.
   */
  #centreOnSearch(lat, lng, label) {
    if (!this.#map) return;
    const provider = this.#mapConfig?.provider;
    const text = (this.dataset.searchResultText || '__location__').replace('__location__', escapeHtml(label));

    if (provider === 'leaflet' || !provider) {
      const { L } = window;
      this.#searchMarker?.remove();
      this.#map.setView([lat, lng], 11);
      this.#searchMarker = L.marker([lat, lng]).addTo(this.#map).bindPopup(`<strong>${text}</strong>`).openPopup();
    } else if (provider === 'mapbox') {
      this.#map.flyTo({ center: [lng, lat], zoom: 11 });
    } else if (provider === 'google') {
      this.#map.setCenter({ lat, lng });
      this.#map.setZoom(11);
    }
  }

  /**
   * Builds a popup HTML string for a reseller.
   * @param {Reseller} reseller - The reseller.
   * @returns {string}
   */
  #popupHtml(reseller) {
    const address = [reseller.address, [reseller.postal_code, reseller.city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .map((line) => escapeHtml(String(line)))
      .join('<br>');

    return `
      <div class="apc-reseller-map__popup">
        <h3 class="apc-reseller-map__popup-title">${escapeHtml(reseller.name ?? '')}</h3>
        <p class="apc-reseller-map__popup-address">${address}</p>
        <a class="apc-reseller-map__popup-link" href="${this.#directionsUrl(reseller)}" target="_blank" rel="noopener">
          ${escapeHtml(this.dataset.navigateText ?? 'Navigate')}
        </a>
      </div>
    `;
  }

  /**
   * Builds a Google Maps directions URL for a reseller.
   * @param {Reseller} reseller - The reseller.
   * @returns {string}
   */
  #directionsUrl(reseller) {
    return `https://www.google.com/maps/dir/?api=1&destination=${reseller.latitude},${reseller.longitude}`;
  }

  /**
   * Shows the empty state and removes the loading state.
   */
  #showEmpty() {
    const { loadingState, emptyState } = this.refs;
    loadingState?.remove();
    if (emptyState) emptyState.hidden = false;
  }

  /**
   * Announces a message to screen readers.
   * @param {string} message - The message.
   */
  #announce(message) {
    const { liveRegion } = this.refs;
    if (liveRegion) liveRegion.textContent = message;
  }

  /**
   * Announces a reseller count to screen readers.
   * @param {number} count - The number of resellers found.
   */
  #announceResults(count) {
    if (count === 0) {
      this.#announce(this.dataset.noResultsText || 'No resellers found');
      return;
    }
    const template = count === 1 ? this.dataset.storesFoundOne : this.dataset.storesFoundOther;
    this.#announce(template ? template.replace('__count__', String(count)) : `${count}`);
  }

  /**
   * Loads an external script once.
   * @param {string} src - The script URL.
   * @returns {Promise<void>}
   */
  #loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Loads an external stylesheet once.
   * @param {string} href - The stylesheet URL.
   */
  #loadStylesheet(href) {
    if (document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
}

/**
 * Escapes a string for safe insertion into HTML.
 * @param {string} value - The raw string.
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * Calculates the great-circle distance between two coordinates in kilometres.
 * @param {number} lat1 - First latitude.
 * @param {number} lon1 - First longitude.
 * @param {number} lat2 - Second latitude.
 * @param {number} lon2 - Second longitude.
 * @returns {number}
 */
function haversine(lat1, lon1, lat2, lon2) {
  const toRad = (value) => (value * Math.PI) / 180;
  const radius = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

if (!customElements.get('apc-reseller-map')) {
  customElements.define('apc-reseller-map', ResellerMap);
}
